import React, {useEffect, useState} from 'react';
import Layout from '../components/Layout';
import {api} from '../../api';

export default function ReviewManager() {
  const [cycles, setCycles] = useState([]);
  const [selectedCycle, setSelectedCycle] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [cycleForm, setCycleForm] = useState(null);
  const [reviewForm, setReviewForm] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [editingReview, setEditingReview] = useState(null);

  const loadCycles = () => api('/hcm/performance-review-cycles').then(r => r.json()).then(setCycles).catch(() => {});
  useEffect(() => { loadCycles(); api('/hcm/employees').then(r => r.json()).then(setEmployees).catch(() => {}); }, []);
  useEffect(() => { if (selectedCycle) api(`/hcm/performance-review-cycles/${selectedCycle}/reviews`).then(r => r.json()).then(setReviews).catch(() => {}); }, [selectedCycle]);

  const ratingLabel = (r) => r ? `${r}/5` : '—';
  const statusBadge = (s) => {
    const cls = s === 'completed' ? 'bg-green-100 text-green-800' : s === 'self_submitted' ? 'bg-blue-100 text-blue-800' : 'bg-yellow-100 text-yellow-800';
    return <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${cls}`}>{s.replace('_', ' ')}</span>;
  };

  return (
    <Layout>
      <div className="bg-white rounded-lg shadow">
        <div className="p-4 md:p-6 border-b flex justify-between items-center">
          <h1 className="text-2xl font-bold text-gray-900">Performance Reviews</h1>
          <button onClick={() => setCycleForm({name: '', startDate: '', endDate: ''})} className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm">New Cycle</button>
        </div>

        <div className="flex">
          <div className="w-64 border-r min-h-[300px]">
            {cycles.map(c => (
              <button key={c.id} onClick={() => setSelectedCycle(c.id)}
                className={`w-full text-left px-4 py-3 border-b hover:bg-gray-50 ${selectedCycle === c.id ? 'bg-indigo-50 border-l-2 border-l-indigo-600' : ''}`}>
                <p className="font-medium text-gray-900 text-sm">{c.name}</p>
                <p className="text-xs text-gray-500">{c.startDate} — {c.endDate}</p>
              </button>
            ))}
            {cycles.length === 0 && <p className="text-gray-500 text-center py-8 text-sm">No cycles yet.</p>}
          </div>

          <div className="flex-1 p-4 md:p-6">
            {!selectedCycle ? (
              <p className="text-gray-500 text-center py-8">Select a review cycle.</p>
            ) : (
              <>
                <div className="flex justify-between items-center mb-4">
                  <h2 className="font-semibold text-gray-900">{cycles.find(c => c.id === selectedCycle)?.name}</h2>
                  <div className="flex gap-2">
                    <button onClick={() => setReviewForm({employeeId: '', reviewerId: '', goals: ''})} className="px-3 py-1 bg-indigo-600 text-white rounded text-sm hover:bg-indigo-700">Add Review</button>
                    <button onClick={async () => { await api(`/hcm/performance-review-cycles/${selectedCycle}`, {method: 'DELETE'}); setSelectedCycle(null); loadCycles(); }}
                      className="px-3 py-1 border border-red-300 text-red-600 rounded text-sm hover:bg-red-50">Delete Cycle</button>
                  </div>
                </div>
                <div className="space-y-3">
                  {reviews.map(r => (
                    <div key={r.id} className="border rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-3">
                          <span className="font-medium text-gray-900">{r.employeeName}</span>
                          {statusBadge(r.status)}
                        </div>
                        <div className="flex gap-2">
                          <button onClick={() => setEditingReview(r)} className="text-sm text-gray-500 hover:text-indigo-600">Edit</button>
                          <button onClick={async () => { await api(`/hcm/performance-reviews/${r.id}`, {method: 'DELETE'}); setSelectedCycle(s => s); }} className="text-sm text-gray-500 hover:text-red-600">Delete</button>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                        <div><span className="text-gray-500">Self:</span> {ratingLabel(r.selfRating)}</div>
                        <div><span className="text-gray-500">Manager:</span> {ratingLabel(r.managerRating)}</div>
                      </div>
                      {r.goals && <p className="text-sm text-gray-500 mt-2">Goals: {r.goals}</p>}
                      {r.selfComments && <p className="text-sm text-gray-400 mt-1 italic">Self: {r.selfComments}</p>}
                      {r.managerComments && <p className="text-sm text-gray-400 mt-1 italic">Manager: {r.managerComments}</p>}
                    </div>
                  ))}
                  {reviews.length === 0 && <p className="text-gray-500 text-center py-4">No reviews in this cycle.</p>}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {cycleForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setCycleForm(null)}>
          <form onSubmit={async e => { e.preventDefault(); await api('/hcm/performance-review-cycles', {method: 'POST', body: JSON.stringify(cycleForm)}); setCycleForm(null); loadCycles(); }} onClick={e => e.stopPropagation()} className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">New Review Cycle</h3>
            <input required value={cycleForm.name} onChange={e => setCycleForm({...cycleForm, name: e.target.value})} placeholder="Cycle name" className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
            <div className="grid grid-cols-2 gap-4">
              <input type="date" value={cycleForm.startDate} onChange={e => setCycleForm({...cycleForm, startDate: e.target.value})} className="px-3 py-2 border border-gray-300 rounded-lg" />
              <input type="date" value={cycleForm.endDate} onChange={e => setCycleForm({...cycleForm, endDate: e.target.value})} className="px-3 py-2 border border-gray-300 rounded-lg" />
            </div>
            <div className="flex justify-end gap-3">
              <button type="button" onClick={() => setCycleForm(null)} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg">Cancel</button>
              <button type="submit" className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">Create</button>
            </div>
          </form>
        </div>
      )}

      {reviewForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setReviewForm(null)}>
          <form onSubmit={async e => { e.preventDefault(); await api(`/hcm/performance-review-cycles/${selectedCycle}/reviews`, {method: 'POST', body: JSON.stringify(reviewForm)}); setReviewForm(null); setSelectedCycle(s => s); }} onClick={e => e.stopPropagation()} className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">Add Review</h3>
            <select required value={reviewForm.employeeId} onChange={e => setReviewForm({...reviewForm, employeeId: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg">
              <option value="">Select employee...</option>
              {employees.map(emp => <option key={emp.id} value={emp.id}>{emp.firstName} {emp.lastName}</option>)}
            </select>
            <textarea value={reviewForm.goals} onChange={e => setReviewForm({...reviewForm, goals: e.target.value})} placeholder="Goals" rows={3} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
            <div className="flex justify-end gap-3">
              <button type="button" onClick={() => setReviewForm(null)} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg">Cancel</button>
              <button type="submit" className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">Add</button>
            </div>
          </form>
        </div>
      )}

      {editingReview && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setEditingReview(null)}>
          <form onSubmit={async e => { e.preventDefault(); await api(`/hcm/performance-reviews/${editingReview.id}`, {method: 'PUT', body: JSON.stringify({managerRating: editingReview.managerRating, managerComments: editingReview.managerComments, goals: editingReview.goals, status: editingReview.status})}); setEditingReview(null); setSelectedCycle(s => s); }} onClick={e => e.stopPropagation()} className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">Edit Review — {editingReview.employeeName}</h3>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Manager Rating (1-5)</label>
              <input type="number" min="1" max="5" value={editingReview.managerRating || ''} onChange={e => setEditingReview({...editingReview, managerRating: parseInt(e.target.value) || null})} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Manager Comments</label>
              <textarea value={editingReview.managerComments || ''} onChange={e => setEditingReview({...editingReview, managerComments: e.target.value})} rows={3} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Goals</label>
              <textarea value={editingReview.goals || ''} onChange={e => setEditingReview({...editingReview, goals: e.target.value})} rows={2} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
            </div>
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={editingReview.status === 'completed'} onChange={e => setEditingReview({...editingReview, status: e.target.checked ? 'completed' : editingReview.status})} className="rounded" />
              <span className="text-sm text-gray-700">Mark as completed</span>
            </label>
            <div className="flex justify-end gap-3">
              <button type="button" onClick={() => setEditingReview(null)} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg">Cancel</button>
              <button type="submit" className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">Save</button>
            </div>
          </form>
        </div>
      )}
    </Layout>
  );
}
